import React from 'react'

import ProfileFormWrapper from 'components/wrappers/ProfileFormWrapper'
import {
  FormControlLabel,
  Stack,
  Typography,
  Skeleton,
  Grid,
} from '@mui/material'
import { BaseField, Option, StyledCheckbox } from 'shared'
import { useFormikContext } from 'formik'
import { ProfileFormValues } from '../config'
import useAttributes from 'utils/hooks/useAttributes'
import { makeStyles } from '@mui/styles'

const useStyles = makeStyles(() => ({
  label: {
    marginBottom: '16px',
  },
}))

const ProfileFormReasonsToJoin = () => {
  const classes = useStyles()
  const { attributes, isLoading } = useAttributes([
    'reasons_to_join',
    'hobbies',
    'hear_about_us',
    'member_reference',
  ])

  const { reasons_to_join, hobbies, hear_about_us, member_reference } =
    attributes || {}
  const { values, setValues, errors } = useFormikContext<ProfileFormValues>()

  const selectedReasons = values.getValuesByAttribute(
    'reasons_to_join',
    'GENERAL',
    values.attributes
  )

  const selectedHearAbout = values.getValuesByAttribute(
    'hear_about_us',
    'GENERAL',
    values.attributes
  )

  const { options = [] } = reasons_to_join || {}
  const { options: hearAboutOptions = [] } = hear_about_us || {}

  const onSelectOption = (
    option: { code: string; text: string },
    allOptions: Option[],
    attribute: string
  ) => {
    const checked = !!allOptions.find(({ value }) => value === option.code)
    if (checked) {
      return setValues(
        values.setAttributeValue({
          attribute,
          category: 'GENERAL',
          value: allOptions
            .filter(({ value }) => value !== option.code)
            .map(({ label, value }) => ({ code: String(value), text: label })),
        })
      )
    }
    return setValues(
      values.setAttributeValue({
        attribute,
        category: 'GENERAL',
        value: [
          ...allOptions.map(({ label, value }) => ({
            code: String(value),
            text: label,
          })),
          option,
        ],
      })
    )
  }

  return (
    <ProfileFormWrapper
      title="Now we want to hear from you!"
      stepDescription="Why are you interested in joining the Hue community? (select up to three answers):"
    >
      {errors.validationErrors?.reasons_to_join ? (
        <Typography fontSize="13px" color="red">
          {errors.validationErrors?.reasons_to_join}
        </Typography>
      ) : null}
      <Stack gap={0.5}>
        {isLoading
          ? Array.from({ length: 6 })
              .fill(0)
              .map(() => {
                return <Skeleton height="30px" variant="text" />
              })
          : null}
        {options.map((option) => {
          const checked = !!selectedReasons.find(
            ({ value }) => value === option.code
          )

          return (
            <FormControlLabel
              key={option.code}
              control={
                <StyledCheckbox
                  checked={checked}
                  onChange={() => {
                    onSelectOption(option, selectedReasons, 'reasons_to_join')
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontWeight: checked ? 600 : 400,
                  }}
                >
                  {option.text}
                </Typography>
              }
            />
          )
        })}
      </Stack>
      {hobbies?.valueType === 'STRING' ? (
        <BaseField
          name="hobbies"
          rows={2}
          multiline
          value={
            values.attributes.find((el) => el.type === 'hobbies')
              ?.value as string
          }
          onChange={(e) => {
            const value = e.target.value
            setValues(
              values.setAttributeValue({
                attribute: 'hobbies',
                category: 'GENERAL',
                value: value,
              })
            )
          }}
          placeholder="Hobbies outside of content creation"
          label="In a few short sentences, tell us about yourself. What are your main interests or hobbies outside of content creation?"
          error={!!errors?.validationErrors?.hobbies}
          helperText={errors?.validationErrors?.hobbies || ''}
          margin="dense"
        />
      ) : null}
      <Grid mt={2}>
        <Typography className={classes.label}>
          How did you hear about our Hue Community program?
        </Typography>
        {errors.validationErrors?.hear_about_us ? (
          <Typography fontSize="13px" color="red">
            {errors.validationErrors?.hear_about_us}
          </Typography>
        ) : null}
        <Stack gap={0.5}>
          {isLoading
            ? Array.from({ length: 6 })
                .fill(0)
                .map(() => {
                  return <Skeleton height="30px" variant="text" />
                })
            : null}
          {hearAboutOptions.map((option) => {
            const checked = !!selectedHearAbout.find(
              ({ value }) => value === option.code
            )

            return (
              <FormControlLabel
                key={option.code}
                control={
                  <StyledCheckbox
                    checked={checked}
                    onChange={() => {
                      onSelectOption(option, selectedHearAbout, 'hear_about_us')
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontWeight: checked ? 600 : 400,
                    }}
                  >
                    {option.text}
                  </Typography>
                }
              />
            )
          })}
        </Stack>
      </Grid>
      {member_reference?.valueType === 'STRING' ? (
        <BaseField
          name="hobbies"
          rows={2}
          multiline
          value={
            values.attributes.find((el) => el.type === 'member_reference')
              ?.value as string
          }
          onChange={(e) => {
            const value = e.target.value
            setValues(
              values.setAttributeValue({
                attribute: 'member_reference',
                category: 'GENERAL',
                value: value,
              })
            )
          }}
          placeholder="Hue Community Member"
          label="Did an existing Hue Community Member refer you to join? If so, please let us know who!"
          error={!!errors?.validationErrors?.member_reference}
          helperText={errors?.validationErrors?.member_reference || ''}
          margin="dense"
        />
      ) : null}
    </ProfileFormWrapper>
  )
}

export default ProfileFormReasonsToJoin
